import React, { useMemo, useState } from 'react';
import { Review } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Maximize2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import ExitDateLineChart from './ExitDateLineChart';
import YearSelector from './YearSelector';
import { prepareChartData, yearHasData } from './utils/chartDataUtils';

interface ExitDateChartProps {
  reviews: Review[];
  compact?: boolean;
  onExpandChart?: () => void;
}

const ExitDateChart = ({ reviews, compact = false, onExpandChart }: ExitDateChartProps) => {
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear - 1, currentYear - 2];
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const isMobile = useIsMobile();

  // Prepare chart data based on reviews and selected year
  const chartData = useMemo(() => 
    prepareChartData(reviews, selectedYear, compact), 
    [reviews, selectedYear, compact]
  );

  const hasDataForYear = chartData.some(data => data.count > 0);

  // Check if a specific year has data (for year selector)
  const checkYearHasData = (year: number): boolean => {
    return yearHasData(reviews, year);
  };

  // Determine chart title based on view mode
  const chartTitle = compact 
    ? "Recent Employee Exits" 
    : (selectedYear === currentYear 
      ? "Employee Exits (Last 12 Months)" 
      : `Employee Exits (${selectedYear})`);

  // Chart height based on view mode
  const chartHeight = compact ? 180 : 300;

  return (
    <Card className={`${compact ? "mb-0" : "mb-8"} w-full`}>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="text-lg font-medium text-redflags-dark">{chartTitle}</CardTitle>
          {!compact && (
            <YearSelector 
              availableYears={availableYears}
              selectedYear={selectedYear}
              onChange={setSelectedYear}
              yearHasData={checkYearHasData}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className={`h-[${chartHeight}px] w-full mt-4`} style={{ height: chartHeight }}>
          {hasDataForYear ? (
            <ExitDateLineChart chartData={chartData} compact={compact} />
          ) : (
            <div className="h-full w-full flex items-center justify-center border border-dashed border-gray-200 rounded-lg bg-gray-50">
              <p className="text-gray-500 text-sm">No data available{!compact && ` for ${selectedYear}`}</p>
            </div>
          )}
        </div>
        
        {compact && (
          <div className="mt-4 flex justify-center">
            <Button 
              variant="outline" 
              size="sm"
              onClick={onExpandChart}
            >
              <Maximize2 className="h-4 w-4 mr-1" />
              View History
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExitDateChart;
